import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Plus, Save, Trash2, X, Edit3, ArrowLeft, RefreshCw } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { WORK_CATEGORIES, PLATFORM_LABEL } from "@/lib/embeds";

export default function AdminTalentWorks() {
  const { id } = useParams();
  const nav = useNavigate();
  const [talent, setTalent] = useState(null);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get(`/admin/talents/${id}/works`).then(setItems).catch(() => setItems([]));
  useEffect(() => {
    api.get(`/talents/${id}`).then(setTalent).catch(() => setTalent(null));
    load();
  }, [id]);

  const save = async () => {
    setError("");
    try {
      const payload = { ...editing, talent_id: id };
      if (editing.id && items.find((it) => it.id === editing.id)) {
        await api.put(`/admin/talents/${id}/works/${editing.id}`, payload);
      } else {
        await api.post(`/admin/talents/${id}/works`, payload);
      }
      setEditing(null); load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || "Error al guardar");
    }
  };

  const del = async (workId) => {
    if (!window.confirm("¿Eliminar esta publicación?")) return;
    await api.delete(`/admin/talents/${id}/works/${workId}`);
    load();
  };

  return (
    <div data-testid="admin-talent-works">
      <button onClick={() => nav("/admin/talents")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Volver a Talentos
      </button>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1">Trabajos & Campañas</h1>
          <p className="text-sm text-muted-foreground">Talento: <strong>{talent?.artist_name || "..."}</strong> · Total: {items.length}</p>
        </div>
        <button onClick={() => setEditing({ url: "", brand: "", category: "Campaña", title: "", date: "", notes: "", active: true, order: items.length })} data-testid="new-work" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> Nueva publicación
        </button>
      </div>

      {editing && (
        <div className="border border-primary rounded-sm p-6 mb-6" data-testid="work-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">{editing.id ? "Editar publicación" : "Nueva publicación"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">URL del post / reel / TikTok / YouTube</label>
              <input type="text" placeholder="https://www.instagram.com/reel/..." value={editing.url || ""} onChange={(e) => setEditing({ ...editing, url: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
            </div>
            <Field label="Marca / Cliente" value={editing.brand} onChange={(v) => setEditing({ ...editing, brand: v })} />
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Categoría</label>
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none">
                {WORK_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Título / descripción" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Fecha (YYYY-MM-DD)</label>
              <input type="date" value={editing.date || ""} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
            </div>
            <Field label="Orden" type="number" value={editing.order ?? 0} onChange={(v) => setEditing({ ...editing, order: Number(v) })} />
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Notas internas</label>
              <textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none resize-none" />
            </div>
            <label className="flex items-center gap-2 h-10">
              <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-primary" />
              <span className="text-sm">Visible en sitio público</span>
            </label>
          </div>
          {error && <p className="text-sm text-destructive mt-3">{error}</p>}
          <div className="mt-5 flex gap-2">
            <button onClick={save} data-testid="save-work" className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold"><Save className="w-4 h-4" /> Guardar</button>
            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-border rounded-sm text-sm">Cancelar</button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
            <tr><th className="text-left p-3">Plataforma</th><th className="text-left p-3">Marca</th><th className="text-left p-3">Categoría</th><th className="text-left p-3">URL</th><th className="text-left p-3">Activo</th><th className="text-right p-3">Acciones</th></tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} className="border-t border-border">
                <td className="p-3 font-semibold">{PLATFORM_LABEL[w.platform] || w.platform}</td>
                <td className="p-3">{w.brand || "—"}</td>
                <td className="p-3">{w.category}</td>
                <td className="p-3 truncate max-w-xs"><a href={w.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{w.url}</a></td>
                <td className="p-3">{w.active ? "Sí" : "No"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing({ ...w })} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-2 hover:border-primary"><Edit3 className="w-3 h-3" /> Editar</button>
                  <button onClick={() => del(w.id)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-destructive/40 text-destructive rounded-sm text-xs font-semibold hover:bg-destructive/10"><Trash2 className="w-3 h-3" /> Eliminar</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Sin publicaciones aún. Click "Nueva publicación" para agregar tu primer reel/TikTok/post.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
    </div>
  );
}
